/**
 * QuickActionsPanel — floating right-side panel that surfaces the most
 * frequent study verbs in one click. Replaces the older FloatingRecorder
 * + FloatingQuickActions FABs (iter186) — both UX surfaces had grown into
 * a "what is this button again?" problem, and the bottom Quick Actions
 * card on the Today tab still exists as a secondary entry point.
 *
 * Design:
 *  - Collapsed: a single round button on the right edge, pinned just
 *    above the mobile bottom-nav (and above the messenger fab on desktop).
 *  - Expanded: a vertical card sliding in from the right with 6 actions,
 *    each a Wave-action chip linking to its destination.
 *  - State persists per-session in localStorage so power users can leave
 *    it open between visits.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, X, Mic, Upload, Lightbulb, BookMarked, Brain, Calendar, Users, PlusSquare,
} from 'lucide-react';

const STORAGE_KEY = 'isp_quick_actions_open_v1';
const QUICK_CAPTURE_EVENT = 'istudypro:quickcapture:open';

const ACTIONS = [
  { to: '/lectures',         icon: Mic,        label: 'Record Lecture', tone: 'peach',    testid: 'qa-record' },
  { to: '/document-uploads', icon: Upload,     label: 'Upload Document', tone: 'sky',     testid: 'qa-upload' },
  { kind: 'quick-capture',   icon: PlusSquare, label: 'Quick Capture',  tone: 'lavender', testid: 'qa-quick-capture' },
  { to: '/learning-bytes',   icon: Lightbulb,  label: 'Flashcards',     tone: 'mint',     testid: 'qa-flashcards' },
  { to: '/review',           icon: BookMarked, label: 'Review',         tone: 'lavender', testid: 'qa-review' },
  { to: '/study-buddy',      icon: Brain,      label: 'AI Buddy',       tone: 'sky',      testid: 'qa-ai-buddy' },
  { to: '/study-rooms',      icon: Users,      label: 'Study Room',     tone: 'mint',     testid: 'qa-study-room' },
  { to: '/schedule',         icon: Calendar,   label: 'Schedule',       tone: 'peach',    testid: 'qa-schedule' },
];

const QuickActionsPanel = () => {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, open ? '1' : '0'); } catch {/* ignore */}
  }, [open]);

  // Bottom offset: lift above the mobile bottom-nav. The .isp-fab-bottom
  // utility class already handles the safe-area math.
  return (
    <>
      {/* Collapsed trigger — always visible, pinned right edge */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="qa-trigger"
            type="button"
            initial={{ opacity: 0, scale: 0.85, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.85, x: 20 }}
            transition={{ type: 'spring', damping: 22, stiffness: 280 }}
            onClick={() => setOpen(true)}
            className="fixed right-5 z-40 w-12 h-12 rounded-full bg-[#002FA7] text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-transform flex items-center justify-center bottom-44 lg:bottom-24"
            title="Quick Actions"
            aria-label="Open quick actions"
            data-testid="quick-actions-trigger"
          >
            <Zap className="w-5 h-5" aria-hidden />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="qa-panel"
            initial={{ opacity: 0, x: 24, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 24, scale: 0.96 }}
            transition={{ type: 'spring', damping: 24, stiffness: 280 }}
            className="fixed right-5 z-40 w-64 bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] shadow-2xl rounded-2xl overflow-hidden bottom-44 lg:bottom-24"
            data-testid="quick-actions-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--isp-glass-border)]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-[#002FA7]" aria-hidden />
                </div>
                <span className="text-sm font-bold text-[var(--isp-heading)] tracking-tight">Quick Actions</span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 -mr-1 text-[var(--isp-muted)] hover:text-[var(--isp-heading)] rounded-lg hover:bg-[var(--isp-glass-inner)]"
                aria-label="Close quick actions"
                data-testid="quick-actions-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 grid gap-2 max-h-[60vh] overflow-y-auto" data-testid="quick-actions-list">
              {ACTIONS.map((action) => {
                const Icon = action.icon;
                const className = `wave-action wave-action--${action.tone} w-full !justify-start gap-3 py-3 px-3.5 text-left`;
                if (action.kind === 'quick-capture') {
                  return (
                    <button
                      key={action.label}
                      type="button"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent(QUICK_CAPTURE_EVENT));
                        setOpen(false);
                      }}
                      className={className}
                      data-testid={action.testid}
                      aria-label={action.label}
                    >
                      <Icon className="w-4.5 h-4.5 flex-shrink-0" aria-hidden />
                      <span className="text-sm font-semibold">{action.label}</span>
                    </button>
                  );
                }
                return (
                  <Link
                    key={action.to + action.label}
                    to={action.to}
                    data-testid={action.testid}
                    aria-label={action.label}
                    onClick={() => setOpen(false)}
                  >
                    <button
                      type="button"
                      className={className}
                    >
                      <Icon className="w-4.5 h-4.5 flex-shrink-0" aria-hidden />
                      <span className="text-sm font-semibold">{action.label}</span>
                    </button>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuickActionsPanel;
